import React, { useMemo } from 'react';
import { Carousel } from '@one-thd/sui-carousel';
import PropTypes from 'prop-types';
import { ListTile } from '@thd-olt-component-react/list-tile';
import classNames from 'classnames';
import NullViewTexts from '../../constants/NullViewTexts';
import NullViewContent from './ListsNullView/NullViewContent';

const ListCarousel = ({
  lists, iconSize, componentId, isDashboardSnapshot, onTileClick, fillHeight
}) => {
  const isSingleList = lists.length === 1;
  // fillHeight: the host sizes the card, so the floor below would only stop it shrinking.
  const hasHeightFloor = isDashboardSnapshot && !fillHeight;

  const listCards = useMemo(() => {
    const { imageUrl, imageAlt } = NullViewTexts;
    const cardWidthClass = `sui-w-72 @lg:sui-w-80 @3xl:sui-w-96 ${isSingleList && '@6xl:sui-w-full'}`;

    const cards = lists?.map((list, index) => (
      <div
        key={index + list.id}
        className={classNames(cardWidthClass, {
          'sui-mr-4': index !== lists.length - 1,
          'sui-grid': isDashboardSnapshot,
        })}
        // 29rem is a real card's own natural height (measured against a populated tile
        // elsewhere with a comparable field set), not an arbitrary number — it keeps this
        // tab's tiles from looking noticeably shorter than sibling cards in other Dashboard
        // Snapshot tabs. Desktop carousel only: the mobile compact list has no sibling row
        // to match, so a floor there is just dead space. A grid minmax() floor, not a fixed
        // height: shorter tiles lift to it, taller ones still grow past it (verified live —
        // no clipping). min-height alone on this div wouldn't reach ListTile's own Card
        // below: a percentage height only resolves against a parent with a definite size,
        // and min-height by itself doesn't make one. No Tailwind utility covers minmax(),
        // so this has to be inline. Skipped under fillHeight: the host then sets the height, and
        // the tile fills it.
        style={hasHeightFloor ? { gridTemplateRows: 'minmax(29rem, auto)' } : undefined}
      >
        <ListTile
          iconSize={iconSize}
          listId={list.id}
          onDelete={() => {}}
          data={list}
          numberOfThumbnails={2}
          enableSharedIcon
          position={index}
          componentId={componentId}
          impressionName="ListsActiveState"
          impressionComponent="Lists"
          isDashboardSnapshot={isDashboardSnapshot}
          onClick={onTileClick}
        />
      </div>
    ));

    if (lists.length < 3) {
      cards.push(
        <div
          key="fallbackContent"
          className={classNames('sui-hidden', cardWidthClass, {
            '@6xl:sui-block': lists.length === 2,
            '@3xl:sui-block': lists.length === 1
          })}
        >
          <NullViewContent carousel />
        </div>
      );

      if (isSingleList) {
        cards.push(
          <div
            key="fallbackImage"
            className={classNames(
              'sui-hidden sui-overflow-hidden',
              cardWidthClass,
              '@6xl:sui-block'
            )}
          >
            <img
              className="sui-block sui-w-full sui-max-h-60 sui-h-auto sui-object-cover sui-object-center"
              src={imageUrl}
              alt={imageAlt}
              width="1400"
              height="900"
            />
          </div>
        );
      }
    }

    return cards;
  }, [lists, iconSize, componentId, isDashboardSnapshot, onTileClick, hasHeightFloor]);

  return (
    <div
      id="ListCarousel"
      data-testid="ListCarousel"
      className={classNames(
        'sui-h-full sui-overflow-x-hidden',
        '[&_#sui-carousel-resets]:sui-h-full',
        '[&_.swiper-wrapper]:sui-h-full',
        {
          '@6xl:[&_.swiper-slide]:sui-basis-1/3 @6xl:[&_.swiper-slide]:sui-w-1/3': isSingleList,
          // Under fillHeight the tiles fill this container, so .swiper is pinned to its full
          // height with a bottom band (!sui-pb-2) that the wrapped scrollbar line lands in,
          // instead of past the clipped edge. Same mechanism as the Quotes carousel. The !s beat
          // the SUI reset's #sui-carousel-resets .swiper / .swiper-wrapper rules.
          '[&_.swiper]:sui-h-full [&_.swiper]:!sui-pb-2 [&_.swiper-wrapper]:!sui-h-full': fillHeight,
        }
      )}
    >
      <Carousel
        type="peek"
        layout="full-bleed"
        containerId="ListCarousel"
        slidesPerView="auto"
        slidesPerGroup={1}
        spaceBetween={0}
        breakpoints={{
          sm: { slidesPerGroup: 1, slidesPerView: 'auto' },
          md: { slidesPerGroup: 1, slidesPerView: 'auto' },
          lg: { slidesPerGroup: 1, slidesPerView: 'auto' },
          xl: { slidesPerGroup: 1, slidesPerView: 'auto' },
          '2xl': { slidesPerGroup: 1, slidesPerView: 'auto' }
        }}
        disableMargin
        className="sui-h-full"
      >
        {listCards}
      </Carousel>
    </div>
  );
};

ListCarousel.propTypes = {
  lists: PropTypes.arrayOf(PropTypes.shape({
    url: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired
  })).isRequired,
  iconSize: PropTypes.string,
  componentId: PropTypes.string,
  isDashboardSnapshot: PropTypes.bool,
  /** Called as (listId, position, source) when a tile's name or thumbnail link is clicked. */
  onTileClick: PropTypes.func,
  fillHeight: PropTypes.bool
};

ListCarousel.defaultProps = {
  iconSize: 'small',
  componentId: undefined,
  isDashboardSnapshot: false,
  onTileClick: undefined,
  fillHeight: false
};

export default ListCarousel;
